import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import TradePage from "./pages/TradePage";
import PortfolioPage from "./pages/PortfolioPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import PropPage from "./pages/PropPage";
import AdminLogin from "./pages/AdminLogin";
import AdminPanel from "./pages/AdminPanel";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { Toaster } from "sonner";

// Telegram WebApp keeps a cached session of the Mini App when reopened via
// ReplyKeyboard WebApp button. This shows stale data (e.g. positions whose
// entry_price was edited via admin). When the WebApp regains visibility after
// being backgrounded for more than 5 seconds, force a hard reload to get fresh state.
function useTelegramFreshReload() {
  useEffect(() => {
    // Only run inside the Telegram Mini App. In a normal desktop/web browser
    // this would reload the page every time the user switches tabs and returns.
    const tg = typeof window !== "undefined" ? window.Telegram?.WebApp : null;
    if (!tg || !tg.initData) return;
    let hiddenAt = 0;
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
      } else if (document.visibilityState === "visible" && hiddenAt) {
        const gone = Date.now() - hiddenAt;
        hiddenAt = 0;
        if (gone > 5000) {
          window.location.reload();
        }
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen flex items-center justify-center text-[#9CA3AF]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen flex items-center justify-center text-[#9CA3AF]">Loading...</div>;
  if (user) return <Navigate to="/trade" replace />;
  return children;
}

function App() {
  useTelegramFreshReload();
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/trade" replace />} />
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
          <Route path="/trade" element={<Protected><TradePage /></Protected>} />
          <Route path="/trade/:symbol" element={<Protected><TradePage /></Protected>} />
          <Route path="/portfolio" element={<Protected><PortfolioPage /></Protected>} />
          <Route path="/leaderboard" element={<Protected><LeaderboardPage /></Protected>} />
          <Route path="/prop" element={<Protected><PropPage /></Protected>} />
          <Route path="/admin/login" element={<AdminAuthProvider><AdminLogin /></AdminAuthProvider>} />
          <Route path="/admin" element={<AdminAuthProvider><AdminPanel /></AdminAuthProvider>} />
        </Routes>
        <Toaster theme="dark" position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
